import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { show as showDrawerLeft } from '../../redux/slices/Drawer/drawerLeft'
import { Stack, Button, Toolbar, IconButton, Typography } from '@mui/material'
import { Tooltip } from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import AccountMenu from '../AccountMenu/AccountMenu'
import Breadcrumb from '../Breadcrumb'
import NotificationBell from '../NotificationBell'
import * as RT from '../../config/routes'
import { useMapContext } from '../../context/MapContext'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'

import styledComp from '@emotion/styled'
import { styled, useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'

import logo_arbolqr from '../../assets/images/logo-app-white.png'

const HEIGHTBORDER = '1px'

const LogosContainer = styledComp.div`
  height: 100%;
  justify-items: center;
`
const shadow = `
0px 2px 4px -1px rgb(0 0 0 / 30%), 
0px 4px 5px 0px rgb(0 0 0 / 0%), 
0px 1px 10px 0px rgb(0 0 0 / 15%)
`

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open' && prop !== 'drawerwidth',
})(({ theme, open, drawerwidth }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerwidth,
    width: `calc(100% - ${drawerwidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const AppBarMenu = ({ city, isAuth, user }) => {
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()
  const theme = useTheme()
  const dispatch = useDispatch()
  let navigate = useNavigate()
  const _state = useSelector(state => state.drawerLeft)
  const treeCode = useSelector(state => state.mapState.popup.code)

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth)
  const { statusData, statusHeaders } = useSelector(state => state.report)

  const { map } = useMapContext()

  const handleOpenCloseDrawerLeft = () => {
    dispatch(showDrawerLeft(!_state.open))
  }

  const handleClickLogin = () => {
    dispatch(showDrawerLeft(false))
    navigate(RT.LOGIN_ROUTE, { state: { returnCanton: city } })
  }

  const handleClickIcon = () => {
    dispatch(showDrawerLeft(false))
    if (treeCode) navigate(buildCantonUrl(city, cantones, `/${treeCode}`))
    else navigate(RT.MAIN)
  }

  const styleAppBar = {
    height: _state.drawerHeight,
    minHeight: _state.drawerMinHeight,
    background:
      'linear-gradient(to bottom, rgba(12, 35, 66),  rgba(12, 35, 66, 0.9), rgba(12, 35, 66, 0.7))',
    backdropFilter: 'blur(6px)',
    boxShadow: shadow,
  }

  const styleToolbar = {
    height: _state.drawerHeight,
    minHeight: `calc(${_state.drawerMinHeight} - ${HEIGHTBORDER})`,
    paddingLeft: '1vw',
    paddingRight: '1vw',
  }

  React.useEffect(() => {
    function handleWindowResize() {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  return (
    <StyledAppBar
      position="fixed"
      color="transparent"
      component="nav"
      sx={styleAppBar}
      open={_state.open}
      drawerwidth={_state.drawerWidth}>
      <Toolbar style={styleToolbar}>
        {Boolean(isAuth) && (
          <IconButton
            onClick={handleOpenCloseDrawerLeft}
            edge="start"
            sx={{
              ml: 0,
              ...(_state.open && { display: 'none' }),
              color: theme.palette.background.default,
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: theme.palette.background.default,
              },
            }}
            disabled={statusHeaders === 'succeeded' && statusData === 'succeeded'}>
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, display: { xs: 'block', sm: 'block' }, height: '56%' }}>
          <LogosContainer
            style={{
              display: !_state.open ? 'flex' : 'none',
              width: 'min-content',
              paddingInline: Boolean(isAuth) ? '8px' : '16px',
            }}>
            <Tooltip title={'ArbolEc'} placement="bottom">
              <img
                src={logo_arbolqr}
                alt="arbolec-logo"
                height="auto"
                style={{ cursor: 'pointer' }}
                onClick={handleClickIcon}
              />
            </Tooltip>
          </LogosContainer>
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', height: '100%', pr: 2 }}>
          <Breadcrumb windowWidth={windowWidth} map={map} />
          {isAuth && <NotificationBell />}
          {!isAuth ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleClickLogin}
              sx={{
                ml: 2,
                fontWeight: 650,
                fontSize: '0.8em',
                textTransform: 'uppercase',
                bgcolor: '#e2e6e9',
                color: '#0c2342',
                '&:hover': {
                  bgcolor: '#cfd6dc',
                  color: '#0c2342',
                },
              }}>
              Acceder
            </Button>
          ) : (
            <AccountMenu user={user} windowWidth={windowWidth} />
          )}
        </Stack>
      </Toolbar>
    </StyledAppBar>
  )
}
export default AppBarMenu
